import type { QueensFacultyId } from './queensUniversityAcademics';

/**
 * Sort programs with higher headcount first, then alphabetical for the rest.
 *
 * **Sources (Queen’s public data — not Genie leasing stats):**
 * - Annual Enrolment Report (degree / faculty buckets):
 *   https://www.queensu.ca/registrar/sites/uregwww/files/uploaded_files/pdfs/Reports/Enrolment%20Reports/Enrolment-Report-2025-26.pdf
 * - IRP student headcount tables: https://www.queensu.ca/planningandbudget/irp/institutional-data/student/enrolment
 * - Arts & Science “students enrolled in plans” Tableau (plan/major level — staff login):
 *   https://www.queensu.ca/artsci/staff-faculty/support/reports
 *
 * **2025–26 undergrad examples from the Enrolment Report (≈ full-time headcount):**
 * Commerce 2,243 · Engineering 3,729 · BA/BAH 5,756 · BSc/BSCH 4,157 · BCMP (Computing) 1,290 ·
 * BHSc on-campus 1,316 · Con-Ed 955 · Kinesiology (BSCH) 680 · Law 668 · Nursing 537 · Medicine 520.
 *
 * The PDF does **not** list every major/plan (e.g. Life Sciences vs Psychology inside BSc). Those ranks
 * below are still partial until we import plan counts from ArtSci/IRP. Smith UG Commerce maps 1:1 to
 * `Business / Commerce`; other lists mix report-backed leaders with reasonable placeholders.
 */
export const QUEENS_PROGRAM_VOLUME_PRIORITY: Readonly<Record<QueensFacultyId, readonly string[]>> = {
  'smith-business': [
    'Business / Commerce',
    'International Business',
    'Accounting',
    'Finance',
    'Management',
    'Entrepreneurship, Innovation & Creativity',
    'MBA / Business Administration',
    'Data Analytics',
    'Analytics',
  ],
  'arts-science': [
    'Life Sciences',
    'Psychology',
    'Biology',
    'Economics',
    'Political Studies',
    'History',
    'English',
    'Kinesiology',
    'Cognitive Science',
    'Global Development Studies',
    'Gender Studies',
    'Sociology',
    'Mathematics',
    'Statistics',
    'Biochemistry',
    'Neuroscience',
  ],
  'smith-engineering': [
    'Computing',
    'Computer Engineering',
    'Software Design',
    'Electrical Engineering',
    'Mechanical and Materials Engineering',
    'Chemical Engineering',
    'Civil Engineering',
    'Engineering Physics',
    'Mechatronics and Robotics Engineering',
    'Biomedical Engineering',
    'Geological Engineering',
    'Mining Engineering',
  ],
  'health-sciences': [
    'Bachelor of Health Sciences',
    'Nursing',
    'Medicine',
    'Kinesiology',
    'Occupational Therapy',
    'Physical Therapy',
    'Public Health Sciences',
    'Life Sciences',
  ],
  law: [
    'Juris Doctor',
    'Civil Law / Common Law',
    'Business / Commerce + Law',
    'Law + Public Administration (JD/MPA)',
    'MBA + Law',
    'LLM Program',
  ],
  education: [
    'Concurrent Education',
    'Indigenous Teacher Education',
    'Technological Education',
    'Teaching English as a Second Language',
  ],
  'graduate-studies': [
    'Biomedical and Molecular Sciences',
    'Aging and Health',
    'Rehabilitation Science',
  ],
};

export function sortProgramsByVolume(facultyId: QueensFacultyId, programs: readonly string[]): string[] {
  const priority = QUEENS_PROGRAM_VOLUME_PRIORITY[facultyId] ?? [];
  const rank = new Map<string, number>();
  priority.forEach((name, index) => {
    if (!rank.has(name)) rank.set(name, index);
  });

  return [...programs].sort((a, b) => {
    const rankA = rank.get(a);
    const rankB = rank.get(b);
    const inA = rankA !== undefined;
    const inB = rankB !== undefined;
    if (inA && inB) return rankA - rankB;
    if (inA) return -1;
    if (inB) return 1;
    return a.localeCompare(b, undefined, { sensitivity: 'base' });
  });
}
